import { useMemo, useState } from 'react'
import { Alert, Card, Col, Row, Segmented, Skeleton, Statistic, theme } from 'antd'
import { BarChartOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { reportWindow } from '@/shared/lib/dayRange'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatBytes, getCurrencyCode, shiftDay } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { fetchRevenueReport, fetchTrafficReport } from '@/features/reports/reportsApi'
import { BarSeriesChart, type ChartPoint } from './BarSeriesChart'

const WINDOWS = [7, 14, 30] as const
type WindowDays = (typeof WINDOWS)[number]

type Mode = 'traffic' | 'revenue'

/**
 * Bosh sahifadagi tarix grafigi — trafik yoki daromad.
 *
 * <h2>Nega bu panel qo'shildi</h2>
 * Bosh sahifada TARIX umuman yo'q edi: uchta "bugungi" raqam va faol
 * sessiyalar soni. Ya'ni "kecha ham shunday edimi", "trafik o'smoqdami yoki
 * tushyaptimi" degan savolga javob berish uchun operator Hisobotlar
 * bo'limiga o'tishi kerak edi. Bitta bugungi son esa o'z-o'zicha ma'nosiz:
 * 890 GB ko'p yoki kam ekani faqat oldingi kunlarga nisbatan bilinadi.
 *
 * <h2>⚠️ `ActiveSessionsTrend` BILAN CHALKASHTIRMANG</h2>
 * U jonli o'lchov: sahifa ochilgandan beri yig'ilgan nuqtalar, sahifa
 * yopilsa yo'qoladi. Bu esa SERVERDAN keladigan tarix (`usage_record`),
 * ya'ni haqiqiy hisobot ma'lumoti. Ikkalasi bir ekranda tursa ham boshqa
 * savolga javob beradi.
 *
 * <h2>⚠️ Daromad BO'SH bo'lishi mumkin va bu XATO EMAS</h2>
 * Daromad butun tashkilotniki, reseller uni ko'rmaydi — backend bo'sh
 * ro'yxat qaytaradi, 403 emas (`reportsApi.fetchRevenueReport` izohi).
 * Shuning uchun bo'sh javob "ma'lumot yo'q" holati sifatida chiziladi.
 */
export function TrafficRevenuePanel() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { can } = usePermissions()

  const [mode, setMode] = useState<Mode>('traffic')
  const [days, setDays] = useState<WindowDays>(14)

  const allowed = can('report.read')

  // ⚠️ Oyna TENANT kalendarida (`dayRange.ts`) — `dayjs()` brauzernikini
  // olardi va grafikning oxirgi ustuni bo'sh kunga tushib qolardi.
  const range = useMemo(() => reportWindow(days), [days])

  const traffic = useQuery({
    queryKey: queryKeys.reports.traffic(range.from, range.to, 'DAY'),
    queryFn: () => fetchTrafficReport(range, 'DAY'),
    enabled: allowed && mode === 'traffic',
  })

  const revenue = useQuery({
    queryKey: queryKeys.reports.revenue(range.from, range.to, 'DAY'),
    queryFn: () => fetchRevenueReport(range, 'DAY'),
    enabled: allowed && mode === 'revenue',
  })

  const active = mode === 'traffic' ? traffic : revenue

  /**
   * ⚠️ SERVER FAQAT MA'LUMOTI BOR KUNLARNI QAYTARADI.
   *
   * Bo'sh kunlar javobda umuman yo'q, ya'ni grafik 14 kunlik oynada 3 ta
   * ustun chizardi va ular bir-biriga yopishib, "har kuni trafik bo'lgan"
   * degan taassurot berardi. Bu yerda oyna to'liq kunlar bilan
   * to'ldiriladi va yo'q kunlar NOL bo'lib ko'rinadi — uzilishni aynan
   * shunday sezish mumkin.
   */
  const points = useMemo<ChartPoint[]>(() => {
    const slots: ChartPoint[] = []
    for (let i = 0; i < days; i += 1) {
      // ⚠️ `shiftDay` — sof KALENDAR arifmetikasi. `dayjs(...).add(i,'day')`
      // brauzer zonasidagi damni surardi; bu yerda esa kerakligi kun
      // yorlig'i, ya'ni zona umuman qatnashmasligi kerak.
      const label = shiftDay(range.from, i)
      slots.push({ key: label, label: label.slice(5), values: {} })
    }
    const index = new Map(slots.map((s) => [s.key, s]))

    if (mode === 'traffic') {
      for (const row of traffic.data ?? []) {
        const slot = index.get(row.label)
        if (slot) slot.values = { upload: row.uploadBytes, download: row.downloadBytes }
      }
      return slots.map((s) => ({
        ...s,
        values: { upload: s.values.upload ?? 0, download: s.values.download ?? 0 },
      }))
    }

    for (const row of revenue.data ?? []) {
      const slot = index.get(row.label)
      if (slot) slot.values = { revenue: row.revenue.amount }
    }
    return slots.map((s) => ({ ...s, values: { revenue: s.values.revenue ?? 0 } }))
  }, [mode, days, range.from, traffic.data, revenue.data])

  // ⚠️ Valyuta JAVOBDAN olinadi, sozlamadan emas: ledger qaysi valyutada
  // yozgan bo'lsa, ekranda ham o'sha turishi kerak. Javob bo'sh bo'lganda
  // (reseller, yoki umuman sotuv bo'lmagan oyna) tenant valyutasi zaxira
  // bo'ladi — bo'sh satr `Intl` da "NaN" ko'rinishini berardi.
  const currency = revenue.data?.[0]?.revenue.currency ?? getCurrencyCode()
  const totals = useMemo(() => {
    if (mode === 'traffic') {
      const rows = traffic.data ?? []
      return {
        first: rows.reduce((s, r) => s + r.uploadBytes, 0),
        second: rows.reduce((s, r) => s + r.downloadBytes, 0),
        third: rows.reduce((s, r) => s + r.totalBytes, 0),
      }
    }
    const rows = revenue.data ?? []
    return {
      first: rows.reduce((s, r) => s + r.revenue.amount, 0),
      second: rows.reduce((s, r) => s + r.txnCount, 0),
      third: 0,
    }
  }, [mode, traffic.data, revenue.data])

  const series =
    mode === 'traffic'
      ? [
          { key: 'download', label: t('reports.columns.download'), color: token.colorPrimary },
          { key: 'upload', label: t('reports.columns.upload'), color: token.colorSuccess },
        ]
      : [{ key: 'revenue', label: t('dashboard.chart.revenue'), color: token.colorSuccess }]

  const format =
    mode === 'traffic'
      ? (value: number) => formatBytes(value)
      : (value: number) => formatMoneyValue(money(value, currency))

  // Ma'lumot BOR-YO'QLIGI javob bo'yicha aniqlanadi, to'ldirilgan
  // nuqtalar bo'yicha emas: to'ldirilganlari har doim `days` ta
  const empty = mode === 'traffic' ? (traffic.data ?? []).length === 0 : (revenue.data ?? []).length === 0

  // ⚠️ ERTA QAYTISH BARCHA HOOK'LARDAN KEYIN. Ilgari u `useQuery` lardan
  // keyin, lekin `useMemo` lardan OLDIN turardi — React qoidasini buzardi
  // (`react-hooks/rules-of-hooks`): huquq holati o'zgarganda hook tartibi
  // o'zgarib, kesh boshqa hook'ga tegishli qiymatni qaytarishi mumkin edi.
  //
  // Huquqi yo'q bo'lsa panel butunlay chizilmaydi: bo'sh karta "ma'lumot
  // yo'q" degan YOLG'ON xabar bo'lardi (`SubscriberTrafficPanel` bilan
  // bir xil qoida). So'rovlar ham yuborilmaydi (`enabled: allowed`).
  if (!allowed) return null

  return (
    <Card
      title={t('dashboard.chart.title')}
      extra={
        <Link to="/reports">{t('dashboard.openReports')}</Link>
      }
      styles={{ body: { paddingTop: 12 } }}
    >
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
        <Col flex="auto">
          <Segmented<Mode>
            value={mode}
            onChange={setMode}
            options={[
              { value: 'traffic', label: t('dashboard.chart.traffic') },
              { value: 'revenue', label: t('dashboard.chart.revenue') },
            ]}
          />
        </Col>
        <Col>
          <Segmented<WindowDays>
            value={days}
            onChange={setDays}
            options={WINDOWS.map((d) => ({ value: d, label: t('subscriber.trafficDays', { count: d }) }))}
          />
        </Col>
      </Row>

      {active.isError ? (
        <Alert type="error" showIcon message={translateError(active.error, t)} />
      ) : active.isPending ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : empty ? (
        <EmptyState
          icon={<BarChartOutlined />}
          title={t('reports.noData')}
          description={t('reports.noDataHint')}
        />
      ) : (
        <>
          <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
            {mode === 'traffic' ? (
              <>
                <Col xs={8}>
                  <Statistic
                    title={t('reports.columns.upload')}
                    value={formatBytes(totals.first)}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col xs={8}>
                  <Statistic
                    title={t('reports.columns.download')}
                    value={formatBytes(totals.second)}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col xs={8}>
                  <Statistic
                    title={t('reports.columns.total')}
                    value={formatBytes(totals.third)}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
              </>
            ) : (
              <>
                <Col xs={12}>
                  <Statistic
                    title={t('dashboard.chart.revenueTotal')}
                    value={formatMoneyValue(money(totals.first, currency))}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title={t('dashboard.chart.txnCount')}
                    value={totals.second}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
              </>
            )}
          </Row>

          <BarSeriesChart
            series={series}
            points={points}
            format={format}
            ariaLabel={mode === 'traffic' ? t('dashboard.chart.trafficAria') : t('dashboard.chart.revenueAria')}
            totalLabel={t('reports.columns.total')}
          />

          {/*
            ⚠️ IZOH ENDI YIG'ILGAN (tester-2, BUG-001).

            Bu yerda 109 (trafik) va 96 (daromad) belgilik butun jumlalar
            ochiq turardi — bosh sahifadagi eng uzun ikki matn aynan shular
            edi (o'lchandi). Grafik ostidagi izoh KERAK: u «bugungi trafik
            qaysi kunga yoziladi» va «nima daromad hisoblanadi» degan
            savollarga javob beradi va bu savollar hisobot bilan ro'yxat
            farq qilganda birinchi bo'lib chiqadi.

            Shuning uchun matn QISQARTIRILMADI, YIG'ILDI: bir qator
            ko'rinadi, qolgani «Batafsil» ostida (`ExpandableHint` matnning
            o'zini kesmaydi).
          */}
          <ExpandableHint
            text={mode === 'traffic' ? t('dashboard.todayExactNote') : t('dashboard.chart.revenueNote')}
            lines={1}
            fontSize={11}
          />
        </>
      )}
    </Card>
  )
}
